"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { motion } from "motion/react";
import Link from "next/link";

// Scrolling Logo Component
const ScrollingLogos = () => {
  const logos = [
    { src: "/landing/hikeventures.webp", name: "Hike Ventures" },
    { src: "/landing/decacorn.png", name: "Decacorn" },
    { src: "/landing/ventureus.png", name: "VentureUs" },
    { src: "/landing/ycombinator.png", name: "Y Combinator" },
    { src: "/landing/underdog_labs.webp", name: "Underdog Labs" },
    { src: "/landing/basis_set_ventures.png", name: "Basis Set Ventures" },
    { src: "/landing/sbxi.svg", name: "SBXi" },
    { src: "/landing/e14.svg", name: "E14" },
  ];

  return (
    <div className="flex justify-start items-center overflow-hidden">
      <motion.div
        className="flex items-center gap-20"
        animate={{
          x: [0, -1152], // 8 logos * 144px (128px + 48px gap) = 1152px
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 30,
            ease: "linear",
          },
        }}
        style={{ width: "3456px" }} // 24 logos * 144px = 3456px (3 sets of 8 logos)
      >
        {/* First set of logos */}
        {logos.map((logo, index) => (
          <div key={`first-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
            <img
              src={logo.src}
              alt={logo.name}
              className="w-auto h-8 max-w-[120px] object-contain opacity-60 transition-all duration-300 filter grayscale"
            />
          </div>
        ))}

        {/* Second set for seamless loop */}
        {logos.map((logo, index) => (
          <div key={`second-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
            <img
              src={logo.src}
              alt={logo.name}
              className="w-auto h-8 max-w-[120px] object-contain opacity-60 transition-all duration-300 filter grayscale"
            />
          </div>
        ))}

        {/* Third set for extra smooth looping */}
        {logos.map((logo, index) => (
          <div key={`third-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
            <img
              src={logo.src}
              alt={logo.name}
              className="w-auto h-8 max-w-[120px] object-contain opacity-60 transition-all duration-300 filter grayscale"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// Large Article Card Component (for hero section)
interface LargeArticleCardProps {
  item: {
    image: string;
    date: string;
    title: string;
    subtitle: string;
    link: string;
  };
}

const LargeArticleCard = ({ item }: LargeArticleCardProps) => {
  return (
    <Link href={item.link} target="_blank" rel="noopener noreferrer">
      <div className="w-full max-w-[400px] lg:max-w-[600px] xl:max-w-[800px] inline-flex flex-col justify-start items-center gap-4 cursor-pointer">
        <img className="self-stretch h-64 lg:h-80 xl:h-96 rounded-2xl object-cover" src={item.image} alt={item.title} />
        <div className="w-full flex-1 flex flex-col justify-start items-start gap-2">
          <div className="self-stretch justify-start text-body text-landing-secondary">
            {new Date(item.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div className="self-stretch justify-start text-tag-lg text-landing-primary font-medium">{item.title}</div>
          <div className="self-stretch justify-start text-body-lg text-landing-secondary">{item.subtitle}</div>
        </div>
      </div>
    </Link>
  );
};

// Small Article Card Component (for editor's picks right side)
interface SmallArticleCardProps {
  item: {
    image: string;
    date: string;
    title: string;
    subtitle: string;
    link: string;
  };
}

const SmallArticleCard = ({ item }: SmallArticleCardProps) => {
  return (
    <Link href={item.link} target="_blank" rel="noopener noreferrer">
      <div className="inline-flex justify-start items-start gap-4 cursor-pointer">
        <div className="w-20 h-20 lg:w-24 lg:h-24 relative bg-slate-100 rounded-2xl overflow-hidden shrink-0">
          <img className="w-full h-full object-cover" src={item.image} alt={item.title} />
        </div>
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-2 min-w-0">
          <div className="justify-start text-body-sm text-landing-secondary">
            {new Date(item.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="justify-start text-body line-clamp-2 text-landing-primary">{item.title}</div>
            <div className="justify-start text-body line-clamp-2 text-landing-secondary">{item.subtitle}</div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Medium Article Card Component (for other sections)
interface MediumArticleCardProps {
  item: {
    image: string;
    date: string;
    title: string;
    subtitle: string;
    link: string;
  };
}

const MediumArticleCard = ({ item }: MediumArticleCardProps) => {
  return (
    <Link href={item.link} target="_blank" rel="noopener noreferrer">
      <div className="w-full flex flex-col justify-start items-start gap-4 cursor-pointer min-w-0">
        <div className="self-stretch h-40 lg:h-52 relative bg-slate-100 rounded-2xl overflow-hidden">
          <img className="w-full h-full object-cover" src={item.image} alt={item.title} />
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch justify-start text-body-sm text-landing-secondary">
            {new Date(item.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="self-stretch justify-start text-tag-lg font-medium line-clamp-2 text-landing-primary">
              {item.title}
            </div>
            <div className="self-stretch justify-start text-body line-clamp-3 text-landing-secondary">
              {item.subtitle}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function Press() {
  const newsItems = [
    {
      image: "/landing/press/bts-innovation-award.jpg",
      date: "2025-01-10",
      title: "AI Event Planning Tool Nowadays Wins BTS America Innovation Faceoff",
      subtitle:
        "Nowadays takes home the People's Choice Award at BTS America Innovation Faceoff, showcasing how AI is transforming corporate event planning!",
      link: "https://www.businesstravelnews.com/Management/AI-Event-Planning-Tool-Nowadays-Wins-BTS-America-Innovation-Faceoff",
    },
    {
      image: "/landing/press/techcrunch.webp",
      date: "2024-12-06",
      title: "Y Combinator alum Nowadays, founded by sisters, raises $2M to automate event planning",
      subtitle: "Nowadays just raised a $2 million party seed round from VCs, including Basis Set Ventures and more!",
      link: "https://techcrunch.com/2024/12/06/y-combinator-alum-nowadays-founded-by-sisters-raises-2m-to-automate-event-planning/",
    },
    {
      image: "/landing/press/wit.png",
      date: "2024-12-12",
      title: "Two Sisters, One Dream: Building Nowadays to transform event planning",
      subtitle:
        "Meet Anna Sun and Amy Yan, two sisters who started with a desire to work together on something impactful. This is their story.",
      link: "https://www.webintravel.com/two-sisters-one-dream-building-nowadays-to-transform-event-planning/",
    },
    {
      image: "/landing/press/skift.jpeg",
      date: "2024-12-13",
      title: "Nowadays' Founders Share How $2 Million Will Help Further Develop Its AI",
      subtitle: "How 40 separate investments are supporting Nowadays' mission to automate event planning.",
      link: "https://meetings.skift.com/2024/12/13/nowadays-founders-share-how-2-million-will-help-further-develop-its-ai/",
    },
    {
      image: "/landing/press/mercerislandfemale.jpg",
      date: "2024-12-06",
      title: "Mercer Island Women Leading Venture Capital",
      subtitle:
        "Amy Yan, co-founder of Nowadays, shares her journey and how Nowadays is changing the event planning industry!",
      link: "https://citylifestyle.com/articles/mercer-island-female-vcs-thankful-for-connection",
    },
    {
      image: "/landing/press/wit.webp",
      date: "2024-10-21",
      title: "Nowadays named People's Choice",
      subtitle:
        "Nowadays received the People's Choice Award in the category of Seedup winners at the 2024 Global Startup Pitch!",
      link: "https://www.phocuswire.com/global-startup-pitch-2024-peoples-choice",
    },
    {
      image: "/landing/press/skiftcover.jpg",
      date: "2024-06-25",
      title: "AI-Powered Venue Booking Is Here",
      subtitle:
        "Listen to our one and only Amy Yan talk about our journey! Take a deep dive into how Nowadays paves way in corporate event planning!",
      link: "https://meetings.skift.com/ai-powered-venue-booking-is-here/",
    },
    {
      image: "/landing/press/A-woman-sitting-at-a-desk-in-a-modern-office-using-a-laptop-computer.jpg",
      date: "2024-06-04",
      title: "Event Tech News Roundup - May 2024",
      subtitle: "Exploring the Latest in AI-Powered Event Planning: A Spotlight on Nowadays, a Y Combinator Graduate.",
      link: "https://meetings.skift.com/event-tech-news-roundup-may-2024/?utm_campaign=Skift%20Meetings%20Stand-Up&utm_medium=email&_hsenc=p2ANqtz-_DbBE53HJDCG5q4EAOxOwTaxGY03_TMH4ND8RnKaSVwnn0ApcPn_u-znkLmVFWegUGhgS5ehT3G8Alpn1BHt8YXTc_jQ&_hsmi=310060287&utm_content=310060287&utm_source=hs_email",
    },
    {
      image: "/landing/press/dognowadays.jpeg",
      date: "2024-05-22",
      title: "Nowadays Product Hunt Launch",
      subtitle: "Product Launch Alert!! Nowadays Handles Venue Coordination and Negotiations Seamlessly",
      link: "https://www.producthunt.com/products/nowadays#nowadays",
    },
    {
      image: "/landing/press/y-combinator-gears1.webp",
      date: "2023-09-06",
      title: "5 Neat AI Startups from Y Combinator's Summer 2023 Batch",
      subtitle: "See how Nowadays stacks up with other innovative YC startups!",
      link: "https://techcrunch.com/2023/09/06/5-neat-ai-startups-from-y-combinators-winter-2023-batch/",
    },
    {
      image: "/landing/press/ycnowadays.jpeg",
      date: "2023-06-13",
      title: "🎉 Nowadays: AI Co-pilot for Event Planning",
      subtitle: "YC Launch: Transform Your Event Planning Experience with Nowadays!",
      link: "https://www.ycombinator.com/launches/J7I-nowadays-ai-copilot-for-event-planning",
    },
  ];

  // Get items by sections
  const recentItems = newsItems.slice(0, 6);
  const olderItems = newsItems.slice(6);

  return (
    <div className="container-top-form">
      {/* Hero Section with Container */}
      <div className="w-full flex">
        <div className="container-page flex flex-col gap-16">
          {/* Header Section */}
          <div className="flex flex-col gap-12">
            <div className="flex flex-col gap-6">
              <div className="text-display font-medium text-landing-primary">In the news</div>
              <div className="flex justify-start items-center gap-32 py-8">
                <div className="w-64 shrink-0  text-body text-landing-secondary">
                  Stay updated with our latest news and launches!
                </div>
                <ScrollingLogos />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section with Container */}
      <div className="w-full flex justify-center items-center">
        <div className="container-page flex flex-col gap-6">
          <div className="h-px bg-gray-200" />

          <div className="flex flex-col justify-start items-start gap-6">
            <div className="flex flex-col lg:flex-row justify-start items-start gap-8 lg:gap-6">
              {/* Large article on left */}
              {recentItems[0] && (
                <div className="lg:w-[713px] shrink-0 flex flex-col justify-start items-start gap-4">
                  <div className="w-full h-64 lg:h-80 bg-slate-100 rounded-2xl overflow-hidden">
                    <img className="w-full h-full object-cover" src={recentItems[0].image} alt={recentItems[0].title} />
                  </div>
                  <div className="flex flex-col justify-start items-start gap-2">
                    <div className="text-body-sm text-landing-secondary">
                      {new Date(recentItems[0].date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <div className="flex flex-col justify-start items-start gap-1">
                      <div className="text-tag-lg font-medium cursor-pointer text-landing-primary">
                        <Link href={recentItems[0].link} target="_blank" rel="noopener noreferrer">
                          {recentItems[0].title}
                        </Link>
                      </div>
                      <div className="text-body text-landing-secondary">{recentItems[0].subtitle}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Small articles on right */}
              <div className="flex flex-col justify-center items-start gap-6">
                {recentItems.slice(1, 4).map((item, index) => (
                  <SmallArticleCard key={index} item={item} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Older Articles Section with Container */}
      <div className="w-full flex justify-center items-center mt-8">
        <div className="container-page flex flex-col gap-6">
          <div className="h-px bg-gray-200" />
          <div className="flex flex-col justify-start items-start gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {olderItems.slice(0, 3).map((item, index) => (
                <MediumArticleCard key={index} item={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
